export const DOC_BLOCK_TYPES = [
  "heading",
  "paragraph",
  "rich_text",
  "text_field",
  "freeform_field",
  "number_field",
  "formula_field",
  "checkbox",
  "signature_line",
  "table",
  "two_column",
  "image",
  "divider",
  "page_break",
] as const;

export type DocBlockType = (typeof DOC_BLOCK_TYPES)[number];

export type DocBlockBase = {
  id: string;
  type: DocBlockType;
  sort_order: number;
};

export type DocHeadingBlock = DocBlockBase & {
  type: "heading";
  level: 1 | 2 | 3;
  text: string;
};

export type DocParagraphBlock = DocBlockBase & {
  type: "paragraph";
  text: string;
};

export type DocRichTextBlock = DocBlockBase & {
  type: "rich_text";
  body: string;
};

export type DocTextFieldBlock = DocBlockBase & {
  type: "text_field";
  label: string;
  variable_key: string;
};

/** Multi-line fillable text — export-time entry (forms, notes, free response). */
export type DocFreeformFieldBlock = DocBlockBase & {
  type: "freeform_field";
  label: string;
  variable_key: string;
};

export type DocNumberFieldBlock = DocBlockBase & {
  type: "number_field";
  label: string;
  variable_key: string;
};

export type DocFormulaFieldBlock = DocBlockBase & {
  type: "formula_field";
  label: string;
  key: string;
  expression: string;
};

export type DocCheckboxBlock = DocBlockBase & {
  type: "checkbox";
  label: string;
  fillable: boolean;
  checked: boolean;
  variable_key: string;
};

export type DocSignatureLineBlock = DocBlockBase & {
  type: "signature_line";
  printed_name_label: string;
  printed_name_variable_key: string;
  signature_caption: string;
  /** Fillable signature (typed) rendered on the signature line. */
  signature_variable_key: string;
  date_label: string;
  /** Fillable date shown inline beside the signature line. */
  date_variable_key: string;
};

export type DocTableBlock = DocBlockBase & {
  type: "table";
  header_row: boolean;
  rows: string[][];
};

export type DocImageBlock = DocBlockBase & {
  type: "image";
  asset_id: string;
  alt: string;
  caption: string;
};

export type DocDividerBlock = DocBlockBase & {
  type: "divider";
};

export type DocNestedBlock =
  | DocHeadingBlock
  | DocParagraphBlock
  | DocRichTextBlock
  | DocTextFieldBlock
  | DocFreeformFieldBlock
  | DocNumberFieldBlock
  | DocFormulaFieldBlock
  | DocCheckboxBlock
  | DocSignatureLineBlock
  | DocTableBlock
  | DocImageBlock
  | DocDividerBlock;

export type DocTwoColumnBlock = DocBlockBase & {
  type: "two_column";
  left: DocNestedBlock[];
  right: DocNestedBlock[];
};

export type DocPageBreakBlock = DocBlockBase & {
  type: "page_break";
};

/** Reserved — parsed/stored but not insertable in V1 editor (§9). */
export type DocComponentRefBlock = {
  id: string;
  type: "component_ref";
  sort_order: number;
  component_id: string;
};

export type DocBlock =
  | DocHeadingBlock
  | DocParagraphBlock
  | DocRichTextBlock
  | DocTextFieldBlock
  | DocFreeformFieldBlock
  | DocNumberFieldBlock
  | DocFormulaFieldBlock
  | DocCheckboxBlock
  | DocSignatureLineBlock
  | DocTableBlock
  | DocTwoColumnBlock
  | DocImageBlock
  | DocDividerBlock
  | DocPageBreakBlock
  | DocComponentRefBlock;

export type DocSnapshotBlock = DocBlock;

export const DOC_BLOCK_TYPE_LABELS: Record<DocBlock["type"], string> = {
  heading: "Heading",
  paragraph: "Paragraph",
  rich_text: "Rich text",
  text_field: "Text field",
  freeform_field: "Freeform field",
  number_field: "Number field",
  formula_field: "Formula field",
  checkbox: "Checkbox",
  signature_line: "Signature line",
  table: "Table",
  two_column: "Two columns",
  image: "Image",
  divider: "Divider",
  page_break: "Page break",
  component_ref: "Component (reserved)",
};

export function isDocEditorBlock(block: DocBlock): block is Exclude<DocBlock, DocComponentRefBlock> {
  return block.type !== "component_ref";
}
